import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Check, Eye, Languages, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { addDays, nights, todayISO } from '../lib/dates'
import { docFormatMoney } from '../locales/documents'
import {
  agesMatter, demoBlockedNights, demoEnquiry, estimateStay, fromPrice,
  DEMO_SLUG, type EnquiryConfig, type EnquiryUnit, type Lang, type StayEstimate,
} from '../lib/enquiry'
import { Button, Card, Field, Input, Select, cx } from '../ui/kit'
import { Logo } from '../ui/Logo'
import { track } from '../lib/track'

/**
 * Public enquiry form — the only screen a guest ever sees, and the only one that
 * renders without a session. It talks exclusively to three SECURITY DEFINER functions
 * (`public_enquiry_config`, `public_enquiry_blocked`, `public_submit_enquiry`); the
 * anon key has no table access at all, so nothing here can read another tenant's data.
 *
 * Availability is only greyed out as a courtesy — the server re-checks it on submit.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 * ZWEI QUELLEN, EINE SEITE
 * ─────────────────────────────────────────────────────────────────────────────────
 * Unter `/e/demo` liegt die Vorschau: dieselbe Seite, dieselben Bausteine, aber die
 * Daten kommen aus `demoEnquiry()` statt vom Server, und ABGESCHICKT WIRD NICHTS.
 * Der Link steht in den Einstellungen des Demobetriebs, und ein Interessent klickt
 * ihn an, um zu sehen, was seine Gäste sehen werden. Eine echte Anfrage, die dabei
 * versehentlich entsteht, wäre schlimmer als die kaputte Seite, die es vorher war.
 *
 * Die Vorschau darf den Store nicht anfassen: diese Seite läuft vor der
 * Anmeldesperre, und wer den Link öffnet, ist womöglich gar nicht im Demomodus.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 * WARUM HIER EIN PREIS STEHT
 * ─────────────────────────────────────────────────────────────────────────────────
 * „Was kostet die Woche im August für uns vier?" ist die Frage, wegen der angerufen
 * wird. Sie unbeantwortet zu lassen verlagert sie nur ans Telefon des Vermieters.
 *
 * Gerechnet wird mit `priceStay()` — derselben Funktion, die das spätere Angebot
 * erstellt. Die Schätzung kann dem Angebot deshalb nicht widersprechen, solange der
 * Vermieter nichts von Hand ändert. Sie ist als Schätzung beschriftet, sie nennt die
 * Posten, aus denen sie besteht, und sie steht NICHT neben einem Knopf, der zu einer
 * Zahlung führt: verbindlich wird der Preis erst mit dem Angebot, das der Vermieter
 * schickt (siehe QuoteAcceptPanel — dort und nur dort entsteht ein Vertrag).
 */

type Config = EnquiryConfig
type Unit = EnquiryUnit

const T = {
  de: {
    loading: 'Wird geladen …',
    notFound: 'Diese Seite ist nicht verfügbar.',
    unit: 'Unterkunft',
    arrival: 'Anreise',
    departure: 'Abreise',
    adults: 'Erwachsene',
    children: 'Kinder',
    childAges: 'Alter der Kinder bei Anreise',
    childAgesHint: 'Für die Kinderermäßigung und die Ortstaxe. Ohne Alter rechnen wir voll.',
    age: 'Alter',
    name: 'Name',
    email: 'E-Mail',
    phone: 'Telefon',
    message: 'Ihre Nachricht',
    optional: 'optional',
    send: 'Anfrage senden',
    sending: 'Wird gesendet …',
    thanks: 'Vielen Dank!',
    thanksBody: 'Ihre Anfrage ist eingegangen. Wir melden uns so bald wie möglich.',
    reference: 'Ihre Referenz',
    nights: 'Nächte',
    unavailable: 'In diesem Zeitraum ist leider nicht alles frei.',
    minNights: 'Mindestaufenthalt: {n} Nächte',
    occupancy: 'Maximal {n} Personen',
    another: 'Weitere Anfrage',
    checking: 'Verfügbarkeit wird geprüft …',
    available: 'Zeitraum ist frei',
    persons: 'Personen',
    bedrooms: '{n} Schlafzimmer',
    from: 'ab {p} pro Nacht',
    estimate: 'Preis, unverbindlich',
    estimateNote: 'Geschätzt aus der aktuellen Preisliste. Verbindlich wird der Preis erst mit dem Angebot, das Sie von uns bekommen.',
    estimateAges: 'Mit dem Alter der Kinder wird es meist günstiger — bitte oben ergänzen.',
    prepayment: 'Davon Anzahlung {p} nach der Bestätigung.',
    langNote: 'Ihre Anfrage, unser Angebot und die Rechnung erhalten Sie auf Deutsch.',
    langSwitch: 'Prefer English?',
    previewTitle: 'Vorschau',
    previewBody: 'So sieht Ihre Anfrageseite für Gäste aus. Alle Angaben stammen aus dem Demobetrieb — es wird nichts gespeichert und nichts abgeschickt.',
    previewDone: 'Nur die Vorschau: es wurde keine Anfrage abgeschickt und keine Buchung angelegt.',
  },
  en: {
    loading: 'Loading …',
    notFound: 'This page is not available.',
    unit: 'Accommodation',
    arrival: 'Arrival',
    departure: 'Departure',
    adults: 'Adults',
    children: 'Children',
    childAges: 'Ages of the children on arrival',
    childAgesHint: 'For the child discount and the tourist tax. Without ages we charge in full.',
    age: 'Age',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    message: 'Your message',
    optional: 'optional',
    send: 'Send enquiry',
    sending: 'Sending …',
    thanks: 'Thank you!',
    thanksBody: 'We have received your enquiry and will reply as soon as we can.',
    reference: 'Your reference',
    nights: 'nights',
    unavailable: 'Not all of those dates are free, unfortunately.',
    minNights: 'Minimum stay: {n} nights',
    occupancy: 'Up to {n} guests',
    another: 'Another enquiry',
    checking: 'Checking availability …',
    available: 'Dates are available',
    persons: 'guests',
    bedrooms: '{n} bedrooms',
    from: 'from {p} per night',
    estimate: 'Price, non-binding',
    estimateNote: 'Estimated from the current rates. The price becomes binding only with the quote we send you.',
    estimateAges: 'Adding the children’s ages usually brings this down — please fill them in above.',
    prepayment: 'Including a deposit payment of {p} after confirmation.',
    langNote: 'Your enquiry, our quote and the invoice will be in English.',
    langSwitch: 'Lieber auf Deutsch?',
    previewTitle: 'Preview',
    previewBody: 'This is how your enquiry page looks to guests. Everything shown comes from the demo property — nothing is stored and nothing is sent.',
    previewDone: 'Preview only: no enquiry was sent and no booking was created.',
  },
} satisfies Record<Lang, Record<string, string>>

export default function EnquiryView() {
  const { slug = '' } = useParams<{ slug: string }>()

  // Die Vorschau, einmal gebaut. `buildDemoDatabase()` datiert jede Buchung relativ zu
  // heute — bei jedem Aufruf neu gerechnet, wäre die Verfügbarkeitsanzeige unruhig.
  const demo = useMemo(() => (slug === DEMO_SLUG ? demoEnquiry() : null), [slug])

  const [lang, setLang] = useState<Lang>(
    () => (navigator.language.toLowerCase().startsWith('de') ? 'de' : 'en'))
  const t = (k: keyof typeof T.de, vars?: Record<string, string | number>) => {
    const raw = T[lang][k]
    return vars ? raw.replace(/\{(\w+)\}/g, (m, n) => String(vars[n] ?? m)) : raw
  }

  const [config, setConfig] = useState<Config | null>(null)
  const [loading, setLoading] = useState(true)
  const [unitId, setUnitId] = useState('')
  const [arrival, setArrival] = useState('')
  const [departure, setDeparture] = useState('')
  const [adults, setAdults] = useState(2)
  const [children, setChildren] = useState(0)
  /** Als Text, weil „noch nichts angegeben" ein eigener Zustand ist und nicht 0 Jahre. */
  const [ages, setAges] = useState<string[]>([])
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [blocked, setBlocked] = useState<string[] | null>(null)
  const [checking, setChecking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ reference: string; nights: number } | null>(null)

  const earliest = config ? addDays(todayISO(), Math.max(config.lead_days, 0)) : todayISO()

  // Die öffentliche Anfrageseite ist für die meisten Gäste der erste Kontakt mit
  // StaySuite überhaupt — und für den Betreiber bisher eine Seite, von der er
  // nicht weiß, ob sie je jemand aufruft.
  useEffect(() => { track('enquiry_open') }, [])

  useEffect(() => {
    if (demo) {
      setConfig(demo.config)
      setLang(demo.config.locale === 'en' ? 'en' : 'de')
      if (demo.config.units[0]) setUnitId(demo.config.units[0].id)
      setLoading(false)
      return
    }
    if (!supabase || !slug) { setLoading(false); return }
    void (async () => {
      const { data, error: e } = await supabase!.rpc('public_enquiry_config', { p_slug: slug })
      if (e || !data) {
        setConfig(null)
      } else {
        const cfg = data as Config
        setConfig(cfg)
        setLang(cfg.locale === 'en' ? 'en' : 'de')
        if (cfg.units[0]) setUnitId(cfg.units[0].id)
      }
      setLoading(false)
    })()
  }, [slug, demo])

  const unit = useMemo(
    () => config?.units.find((u) => u.id === unitId), [config, unitId])

  // Re-check availability whenever the range or unit changes.
  const checkAvailability = useCallback(async () => {
    if (!unitId || !arrival || !departure || departure <= arrival) {
      setBlocked(null)
      return
    }
    // In der Vorschau aus den Demodaten, nach denselben Regeln wie auf dem Server.
    if (demo) { setBlocked(demoBlockedNights(demo.db, unitId, arrival, departure)); return }
    if (!supabase) { setBlocked(null); return }
    setChecking(true)
    const { data, error: e } = await supabase.rpc('public_enquiry_blocked', {
      p_slug: slug, p_unit: unitId, p_from: arrival, p_to: departure,
    })
    setChecking(false)
    if (e) { setBlocked(null); return }
    setBlocked((data as string[] | null) ?? [])
  }, [slug, unitId, arrival, departure, demo])

  useEffect(() => {
    const id = setTimeout(checkAvailability, 250)
    return () => clearTimeout(id)
  }, [checkAvailability])

  const stayNights = arrival && departure && departure > arrival
    ? nights(arrival, departure) : 0
  const rangeFree = blocked !== null && blocked.length === 0 && stayNights > 0
  const rangeTaken = blocked !== null && blocked.length > 0

  const tooShort = !!unit && stayNights > 0 && stayNights < unit.min_nights
  const tooMany = !!unit && adults + children > unit.max_occupancy

  const askAges = agesMatter(config?.pricing)

  // Der Ab-Preis kostet einen Durchlauf über ein Jahr je Einheit. Einmal beim Laden,
  // nicht bei jedem Tastendruck im Formular.
  const fromPrices = useMemo(() => {
    const map = new Map<string, number | null>()
    if (config) for (const u of config.units) map.set(u.id, fromPrice(config, u))
    return map
  }, [config])

  /** Vollständig oder gar nicht — halbe Angaben ergäben eine erfundene Ermäßigung. */
  const childAges = useMemo(() => {
    const given = Array.from({ length: children }, (_, i) => ages[i] ?? '')
    if (given.some((v) => v === '')) return []
    return given.map((v) => Number(v))
  }, [ages, children])

  const estimate: StayEstimate | null = useMemo(() => {
    if (!config || !unit) return null
    return estimateStay({
      config, unit, arrival, departure, adults, children, childrenAges: childAges, lang,
    })
  }, [config, unit, arrival, departure, adults, children, childAges, lang])

  const money = (n: number) => docFormatMoney(lang, n, config?.currency ?? 'EUR')

  const canSubmit = !!unit && rangeFree && !tooShort && !tooMany
    && name.trim().length > 1 && /\S+@\S+\.\S+/.test(email) && !busy

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    setError(null)

    /*
     * In der Vorschau endet es hier. Kein Serveraufruf, keine Buchung, keine Zeile in
     * irgendeiner Tabelle — der Interessent bekommt die Bestätigung zu sehen, die
     * seine Gäste sehen würden, und sonst passiert nichts.
     */
    if (demo) {
      setTimeout(() => {
        setBusy(false)
        setDone({ reference: 'DEMO-0001', nights: stayNights })
      }, 500)
      return
    }

    if (!supabase) { setBusy(false); return }
    const { data, error: err } = await supabase.rpc('public_submit_enquiry', {
      p_slug: slug, p_unit: unitId, p_arrival: arrival, p_departure: departure,
      p_adults: adults, p_children: children, p_name: name, p_email: email,
      p_phone: phone, p_message: message, p_lang: lang,
      p_children_ages: childAges.length === children && children > 0 ? childAges : null,
    })
    setBusy(false)
    if (err) { setError(err.message); return }
    const res = data as { reference: string; nights: number }
    setDone(res)
  }

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center text-muted gap-2">
        <Loader2 size={18} className="animate-spin" /> {t('loading')}
      </div>
    )
  }

  if (!config) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-6 text-center">
        <p className="text-muted">{t('notFound')}</p>
      </div>
    )
  }

  const accent = config.accent || '#0f766e'

  return (
    <div className="min-h-dvh" style={{ ['--color-accent' as string]: accent }}>
      <header className="px-4 pt-safe pb-4 border-b border-border bg-surface">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <Logo size={40} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <h1 className="font-semibold truncate">{config.name}</h1>
            <p className="text-xs text-muted">{config.intro?.[lang] ?? ''}</p>
          </div>
          {/* Das Symbol ist der Unterschied zwischen „DE EN" und zwei Knöpfen, deren
              Zweck man erraten muss — der Betreiber hat den Umschalter nicht gefunden. */}
          <div className="flex items-center gap-1 shrink-0">
            <Languages size={16} className="text-muted" aria-hidden />
            {(['de', 'en'] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                aria-label={l === 'de' ? 'Deutsch' : 'English'}
                className={cx('px-2 py-1 rounded-lg text-xs font-medium uppercase',
                  lang === l ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-muted')}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="p-4 max-w-xl mx-auto space-y-3">
        {demo && (
          <div className="rounded-xl border border-warn bg-warn-soft text-warn px-3 py-2
                          flex gap-2 items-start">
            <Eye size={16} className="shrink-0 mt-0.5" />
            <p className="text-sm">
              <strong>{t('previewTitle')}</strong> — {t('previewBody')}
            </p>
          </div>
        )}

        {done ? (
          <Card className="p-6 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-ok-soft text-ok mx-auto
                            flex items-center justify-center">
              <Check size={24} />
            </div>
            <h2 className="text-lg font-semibold">{t('thanks')}</h2>
            <p className="text-sm text-muted">{t('thanksBody')}</p>
            <p className="text-sm">
              {t('reference')}: <strong>{done.reference}</strong>
            </p>
            {demo && (
              <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
                {t('previewDone')}
              </p>
            )}
            <Button onClick={() => { setDone(null); setMessage('') }}>{t('another')}</Button>
          </Card>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Card className="p-4 space-y-3">
              {/*
                Eine Liste statt eines Auswahlfelds: „Maximal 4 Personen" ist keine
                Antwort auf die Frage, welche der sechs Wohnungen die richtige ist.
                Ausstattung, Größe und der Ab-Preis sind es.
              */}
              <fieldset>
                <legend className="block text-xs font-medium text-muted mb-1.5">
                  {t('unit')}
                </legend>
                <div className="space-y-2">
                  {config.units.map((u) => (
                    <UnitChoice
                      key={u.id}
                      unit={u}
                      selected={u.id === unitId}
                      onSelect={() => setUnitId(u.id)}
                      lang={lang}
                      label={t}
                      price={fromPrices.get(u.id) ?? null}
                      money={money}
                    />
                  ))}
                </div>
              </fieldset>

              <div className="grid grid-cols-2 gap-3">
                <Field label={t('arrival')}>
                  <Input type="date" required min={earliest} value={arrival}
                    onChange={(e) => setArrival(e.target.value)} />
                </Field>
                <Field label={t('departure')}>
                  <Input type="date" required min={arrival || earliest} value={departure}
                    onChange={(e) => setDeparture(e.target.value)} />
                </Field>
              </div>

              {checking && (
                <p className="text-sm text-muted flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> {t('checking')}
                </p>
              )}
              {!checking && rangeFree && (
                <p className="text-sm bg-ok-soft text-ok rounded-xl px-3 py-2">
                  {t('available')} · {stayNights} {t('nights')}
                </p>
              )}
              {!checking && rangeTaken && (
                <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
                  {t('unavailable')}
                </p>
              )}
              {tooShort && unit && (
                <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
                  {t('minNights', { n: unit.min_nights })}
                </p>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label={t('adults')}>
                  <Input type="number" min={1} max={20} value={adults}
                    onChange={(e) => setAdults(Number(e.target.value))} />
                </Field>
                <Field label={t('children')}>
                  <Input type="number" min={0} max={20} value={children}
                    onChange={(e) => setChildren(Math.max(0, Number(e.target.value)))} />
                </Field>
              </div>

              {/*
                Nur fragen, wenn die Antwort etwas ändert: ohne Kinderermäßigung und
                ohne Altersbefreiung bei der Ortstaxe rechnet das Alter dasselbe wie
                keines — und eine Familie füllt auf dem Handy kein Feld aus, das nichts
                bewirkt. Auswahl statt Zahlenfeld, damit ein Daumen genügt.
              */}
              {askAges && children > 0 && (
                <fieldset>
                  <legend className="block text-xs font-medium text-muted mb-1.5">
                    {t('childAges')}
                  </legend>
                  <div className="grid grid-cols-4 gap-2">
                    {Array.from({ length: Math.min(children, 20) }, (_, i) => (
                      <Select
                        key={i}
                        aria-label={`${t('age')} ${i + 1}`}
                        value={ages[i] ?? ''}
                        onChange={(e) => setAges((prev) => {
                          const next = [...prev]
                          next[i] = e.target.value
                          return next
                        })}
                      >
                        <option value="">–</option>
                        {Array.from({ length: 18 }, (_, a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </Select>
                    ))}
                  </div>
                  <span className="block text-xs text-muted mt-1">{t('childAgesHint')}</span>
                </fieldset>
              )}

              {tooMany && unit && (
                <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
                  {t('occupancy', { n: unit.max_occupancy })}
                </p>
              )}
            </Card>

            {estimate && !tooMany && (
              <Card className="p-4 space-y-2">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-muted">{t('estimate')}</span>
                  <strong className="text-xl">{money(estimate.gross)}</strong>
                </div>
                {/* Die Posten, aus denen die Zahl besteht. Eine Summe ohne Herkunft
                    beantwortet die Frage nicht, sondern erzeugt die nächste. */}
                <div className="text-sm text-muted divide-y divide-border">
                  {estimate.lines.map((l, i) => (
                    <div key={i} className="flex justify-between gap-3 py-1">
                      <span>{l.label}</span>
                      <span className="tabular-nums shrink-0">{money(l.gross)}</span>
                    </div>
                  ))}
                </div>
                {estimate.agesMissing && (
                  <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
                    {t('estimateAges')}
                  </p>
                )}
                {estimate.prepayment > 0 && (
                  <p className="text-xs text-muted">
                    {t('prepayment', { p: money(estimate.prepayment) })}
                  </p>
                )}
                <p className="text-xs text-muted">{t('estimateNote')}</p>
              </Card>
            )}

            <Card className="p-4 space-y-3">
              <Field label={t('name')}>
                <Input required value={name} autoComplete="name"
                  onChange={(e) => setName(e.target.value)} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('email')}>
                  <Input required type="email" value={email} autoComplete="email"
                    onChange={(e) => setEmail(e.target.value)} />
                </Field>
                <Field label={`${t('phone')} (${t('optional')})`}>
                  <Input type="tel" value={phone} autoComplete="tel"
                    onChange={(e) => setPhone(e.target.value)} />
                </Field>
              </div>
              <Field label={`${t('message')} (${t('optional')})`}>
                <Input value={message} onChange={(e) => setMessage(e.target.value)} />
              </Field>
            </Card>

            {error && (
              <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2">{error}</p>
            )}

            <Button type="submit" variant="primary" className="w-full" disabled={!canSubmit}>
              {busy ? t('sending') : t('send')}
            </Button>

            {/*
              Die Sprachwahl entscheidet mehr, als der Umschalter oben verrät: sie geht
              als `p_lang` mit und bestimmt die Sprache von Angebot und Rechnung. Das
              gehört dorthin, wo entschieden wird — und der Hinweis auf die andere
              Sprache steht IN dieser Sprache, damit ihn liest, wer ihn braucht.
            */}
            <p className="text-center text-xs text-muted">
              {t('langNote')}{' '}
              <button
                type="button"
                className="underline"
                onClick={() => setLang(lang === 'de' ? 'en' : 'de')}
              >
                {t('langSwitch')}
              </button>
            </p>
          </form>
        )}

        <p className="text-center text-xs text-muted mt-6">
          {config.name} · StaySuite
        </p>
      </main>
    </div>
  )
}

/**
 * Eine Einheit zur Auswahl. Sie zeigt, was ein Gast zum Entscheiden braucht:
 * Ausstattung, Größe, wie viele hineinpassen und ab welchem Preis es losgeht.
 */
function UnitChoice({
  unit, selected, onSelect, lang, label, price, money,
}: {
  unit: Unit
  selected: boolean
  onSelect: () => void
  lang: Lang
  label: (k: keyof typeof T.de, vars?: Record<string, string | number>) => string
  price: number | null
  money: (n: number) => string
}) {
  // Eine Zeile, und nur was die Wahl beeinflusst. Der Mindestaufenthalt steht nicht
  // hier, sondern erscheint als Hinweis, sobald der gewählte Zeitraum zu kurz ist —
  // sechs Einheiten mit je vier Zeilen sind auf einem Telefon eine Wand.
  const facts = [
    unit.category,
    label('occupancy', { n: unit.max_occupancy }),
    unit.bedrooms ? label('bedrooms', { n: unit.bedrooms }) : null,
    unit.size_sqm ? `${unit.size_sqm} m²` : null,
  ].filter(Boolean) as string[]

  const description = unit.description?.[lang] ?? ''
  const amenities = unit.amenities ?? []

  return (
    <label
      className={cx(
        'block rounded-xl border p-3 cursor-pointer',
        selected ? 'border-accent bg-accent-soft' : 'border-border bg-surface-2',
      )}
    >
      <div className="flex items-start gap-3">
        <input
          type="radio"
          name="unit"
          aria-label={unit.name}
          className="mt-1 accent-accent"
          checked={selected}
          onChange={onSelect}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-medium">{unit.name}</span>
            {price !== null && (
              <span className="text-sm text-muted shrink-0">
                {label('from', { p: money(price) })}
              </span>
            )}
          </div>
          <div className="text-xs text-muted mt-0.5">{facts.join(' · ')}</div>
          {/* Der Beschreibungstext nur bei der gewählten Einheit: er ist ein Absatz,
              und sechs Absätze übereinander liest niemand. */}
          {description && selected && <p className="text-sm mt-1.5">{description}</p>}
          {amenities.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {amenities.map((a) => (
                <span key={a}
                  className="px-2 py-0.5 rounded-lg bg-surface border border-border text-xs">
                  {a}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </label>
  )
}
